import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { OutboundCampaign } from '@/types/TOutbound';
import {
	setCampaigns,
	addCampaign,
	updateCampaign,
	removeCampaign,
	setCurrentCampaign,
	updateCurrentCampaign,
	resetCurrentCampaign,
} from '@/store/outbound/slice';
import {
	getAllCampaigns,
	getCampaignsArray,
	getCurrentCampaign,
	getCampaignById,
	getCampaignsByStatus,
} from '@/store/outbound/selector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	createOutboundTask,
	updateOutboundTask,
	updateOutboundTaskStatus,
	getOutboundTasks,
	deleteOutboundTask,
	getWorkflows,
	createWorkflow,
	updateWorkflow,
	deleteWorkflow,
} from '@/api/crm/outbound';

export const useCampaignById = (id: string) => {
	return useSelector(getCampaignById(id));
};

export const useCampaignsByStatus = (status: 'Active' | 'Paused' | 'Draft') => {
	return useSelector(getCampaignsByStatus(status));
};

export const useOutbound = () => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const campaigns = useSelector(getAllCampaigns);
	const campaignsArray = useSelector(getCampaignsArray);
	const currentCampaign = useSelector(getCurrentCampaign);

	const createTaskMutation = useMutation({
		mutationFn: (taskData: any) => createOutboundTask(taskData, false),
		onSuccess: () => {
			message.success('Task created successfully!');
			queryClient.invalidateQueries({ queryKey: ['outbound-tasks'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to create task');
		},
	});

	const updateTaskMutation = useMutation({
		mutationFn: ({ taskId, data }: { taskId: string; data: any }) =>
			updateOutboundTask(taskId, data, false),
		onSuccess: () => {
			message.success('Task updated successfully!');
			queryClient.invalidateQueries({ queryKey: ['outbound-tasks'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to update task');
		},
	});

	const updateStatusMutation = useMutation({
		mutationFn: ({ taskId, status }: { taskId: string; status: 'Active' | 'Paused' | 'Draft' }) =>
			updateOutboundTaskStatus(taskId, status, false),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['outbound-tasks'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to update task status');
		},
	});

	const deleteTaskMutation = useMutation({
		mutationFn: (taskId: string) => deleteOutboundTask(taskId, false),
		onSuccess: () => {
			message.success('Task deleted successfully!');
			queryClient.invalidateQueries({ queryKey: ['outbound-tasks'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to delete task');
		},
	});

	const getTaskMutation = useMutation({
		mutationFn: () => getOutboundTasks(false),
		onSuccess: (data) => {
			dispatch(setCampaigns(data.tasks));
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to fetch tasks');
		},
	});

	const getWorkflowsMutation = useMutation({
		mutationFn: (clientId: string) => getWorkflows(clientId, false),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['workflows'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to fetch workflows');
		},
	});

	const createWorkflowMutation = useMutation({
		mutationFn: ({ clientId, workflowData }: { clientId: string; workflowData: any }) =>
			createWorkflow(clientId, workflowData, false),
		onSuccess: () => {
			message.success('Workflow created successfully!');
			queryClient.invalidateQueries({ queryKey: ['workflows'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to create workflow');
		},
	});

	const updateWorkflowMutation = useMutation({
		mutationFn: ({
			clientId,
			workflowId,
			workflowData,
		}: {
			clientId: string;
			workflowId: string;
			workflowData: any;
		}) => updateWorkflow(clientId, workflowId, workflowData, false),
		onSuccess: () => {
			message.success('Workflow updated successfully!');
			queryClient.invalidateQueries({ queryKey: ['workflows'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to update workflow');
		},
	});

	const deleteWorkflowMutation = useMutation({
		mutationFn: ({ clientId, workflowId }: { clientId: string; workflowId: string }) =>
			deleteWorkflow(clientId, workflowId, false),
		onSuccess: () => {
			message.success('Workflow deleted successfully!');
			queryClient.invalidateQueries({ queryKey: ['workflows'] });
		},
		onError: (error: any) => {
			message.error(error.message || 'Failed to delete workflow');
		},
	});

	const actions = {
		setCampaigns: (campaigns: OutboundCampaign[]) => dispatch(setCampaigns(campaigns)),
		addCampaign: (campaign: OutboundCampaign) => dispatch(addCampaign(campaign)),
		updateCampaign: (id: string, data: Partial<OutboundCampaign>) =>
			dispatch(updateCampaign({ id, data })),
		removeCampaign: (id: string) => dispatch(removeCampaign(id)),
		setCurrentCampaign: (campaign: OutboundCampaign | null) =>
			dispatch(setCurrentCampaign(campaign)),
		updateCurrentCampaign: (data: Partial<OutboundCampaign>) =>
			dispatch(updateCurrentCampaign(data)),
		resetCurrentCampaign: () => dispatch(resetCurrentCampaign()),
	};

	return {
		campaigns,
		campaignsArray,
		currentCampaign,
		createTaskMutation,
		updateTaskMutation,
		updateStatusMutation,
		deleteTaskMutation,
		getTaskMutation,
		getWorkflowsMutation,
		createWorkflowMutation,
		updateWorkflowMutation,
		deleteWorkflowMutation,
		...actions,
	};
};
